// =================================================================
// AppLayout.js - Layout Principal 3 Colonnes Oracle TRYA HARMONISÉ
// Version Professionnelle avec Toggle CM et Boutons Header uniformisés
// =================================================================

import React, { useState } from 'react';
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import GlobalCostIndicator from './GlobalCostIndicator';
import UserKeyIndicator from './UserKeyIndicator';
import NotificationBell from '../notifications/NotificationBell';

const AppLayout = ({ children, user, onLogout, showFilesSidebar = true, hideSidebar = false, onNavigate }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filesSidebarVisible, setFilesSidebarVisible] = useState(true);
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="oracle-layout">
      <style>{`
        .oracle-layout {
          display: flex;
          height: 100vh;
          width: 100%;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: var(--bg);
          color: var(--text);
        }

        .sidebar-left {
          width: 320px;
          background: var(--sidebar-gradient);
          border-right: 1px solid var(--sidebar-border);
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          transition: width 0.3s ease;
          overflow: hidden;
          position: relative;
        }

        /* Mode nuit : fond dégradé bleu nuit discret + bordure droite subtile. */
        body.dark .sidebar-left {
          background: linear-gradient(180deg,
            rgba(30,58,138,0.10) 0%,
            rgba(15,23,42,0.92) 50%,
            rgba(30,58,138,0.08) 100%) !important;
          border-right: 1px solid rgba(96,165,250,0.18) !important;
          box-shadow: 1px 0 14px -6px rgba(59,130,246,0.22);
        }

        .sidebar-left.collapsed {
          width: 64px;
        }

        .sidebar-left.hidden {
          display: none;
        }

        /* Mode icônes — cacher textes dans la sidebar collapsed */
        .sidebar-left.collapsed .sidebar-logo img {
          height: 28px;
        }
        .sidebar-left.collapsed .sidebar-logo {
          padding: 12px 8px !important;
        }
        .sidebar-left.collapsed .sidebar-profile-btn span {
          display: none;
        }
        .sidebar-left.collapsed .sidebar-profile-btn {
          justify-content: center !important;
          padding: 10px 0 !important;
        }
        .sidebar-left.collapsed .sidebar-user-info {
          display: none;
        }
        .sidebar-left.collapsed .sidebar-nav-container {
          padding: 0 2px !important;
        }

        .chat-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          background: var(--main-bg);
          position: relative;
        }

        .chat-header {
          background: var(--topbar-bg);
          border-bottom: 1px solid var(--topbar-border);
          padding: 10px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-height: 48px;
        }

        .toggle-cm {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .memory-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--text-muted);
          background: var(--card-bg);
          padding: 8px 16px;
          border-radius: 8px;
          border-left: 3px solid var(--accent);
        }

        .sidebar-right {
          width: 320px;
          background: var(--sidebar-gradient);
          border-left: 1px solid var(--sidebar-border);
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          transition: width 0.3s ease;
        }

        .sidebar-right.sidebar-hidden {
          display: none;
        }

        /* Header Toolbar */
        .header-toolbar {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-left: 12px;
        }

        @media (max-width: 1024px) {
          .sidebar-left, .sidebar-right {
            position: absolute;
            top: 0;
            bottom: 0;
            z-index: 1000;
            box-shadow: 2px 0 10px rgba(0,0,0,0.1);
          }

          .sidebar-left {
            left: 0;
            transform: translateX(-100%);
          }

          .sidebar-right {
            right: 0;
            transform: translateX(100%);
          }

          .sidebar-left:not(.collapsed),
          .sidebar-right:not(.sidebar-hidden) {
            transform: translateX(0);
          }
        }
      `}</style>

      {/* Sidebar Navigation Gauche */}
      <div className={`sidebar-left ${hideSidebar ? 'hidden' : sidebarCollapsed ? 'collapsed' : ''}`}>
        {children.sidebar}

        {/* User Info */}
        <div className="sidebar-user-info" style={{
          padding: '16px',
          borderTop: '1px solid var(--sidebar-border)',
          background: isDark ? 'rgba(17,17,24,0.8)' : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Connecté: {user?.username} ({
              user?.role === 'admin_general' ? 'Admin' :
              user?.role === 'oracle_fondateur' ? 'Fondateur' :
              user?.profile_template_code === 'FD' ? 'Fondateur Directeur' :
              'Membre'
            })
          </div>
          <button
            onClick={onLogout}
            style={{
              marginTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              fontSize: '11px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'all 0.2s',
              justifyContent: 'flex-end',
              width: '100%'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--accent-light)';
              e.target.style.color = 'var(--accent)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'none';
              e.target.style.color = 'var(--text-secondary)';
            }}
          >
            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 2.75C2 1.784 2.784 1 3.75 1h2.5a.75.75 0 010 1.5h-2.5a.25.25 0 00-.25.25v10.5c0 .138.112.25.25.25h2.5a.75.75 0 010 1.5h-2.5A1.75 1.75 0 012 13.25V2.75z"/>
              <path d="m10.44 9.5 1.97-1.97a.75.75 0 000-1.06L10.44 4.5a.75.75 0 10-1.06 1.06l.97.97H6a.75.75 0 000 1.5h4.35l-.97.97a.75.75 0 101.06 1.06z"/>
            </svg>
            quitter
          </button>
        </div>
      </div>

      {/* Zone Chat Centrale */}
      <div className="chat-main">

        {/* Header Chat — Topbar avec Toggle Thème */}
        <div className="chat-header" style={hideSidebar ? { display: 'none' } : {}}>
          <div className="toggle-cm">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              title={sidebarCollapsed ? 'Ouvrir la sidebar' : 'Réduire la sidebar'}
              className="itriia-header-icon-btn"
              aria-label={sidebarCollapsed ? 'Ouvrir la sidebar' : 'Réduire la sidebar'}
            >
              {sidebarCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            </button>

            {/* Cloche notif — placée à gauche, juste avant le toggle Jour/Nuit */}
            <NotificationBell onNavigate={onNavigate} />

            {/* Toggle Jour/Nuit */}
            <div className="theme-toggle" onClick={toggleTheme} title={isDark ? 'Passer en mode Jour' : 'Passer en mode Nuit'}>
              <Sun size={14} style={{ color: 'var(--toggle-icon-sun)' }} />
              <div className="toggle-track">
                <div className="toggle-knob" />
              </div>
              <Moon size={14} style={{ color: 'var(--toggle-icon-moon)' }} />
            </div>
          </div>

          {/* Header toolbar */}
          <div className="header-toolbar">
            <UserKeyIndicator onNavigate={onNavigate} />
            <GlobalCostIndicator />
          </div>

          {showFilesSidebar && (
            <button
              onClick={() => setFilesSidebarVisible(!filesSidebarVisible)}
              title={filesSidebarVisible ? 'Masquer les fichiers' : 'Afficher les fichiers'}
              className="itriia-header-icon-btn"
              aria-label={filesSidebarVisible ? 'Masquer les fichiers' : 'Afficher les fichiers'}
            >
              {filesSidebarVisible ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
            </button>
          )}
        </div>

        {/* Contenu Chat Principal */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {children.main}
        </div>
      </div>

      {/* Sidebar Files Droite */}
      {showFilesSidebar && (
        <div className={`sidebar-right ${!filesSidebarVisible ? 'sidebar-hidden' : ''}`}>
          {children.filesSidebar}
        </div>
      )}
    </div>
  );
};

export default AppLayout;
